/**
 * Teléfonos y correos: una sola regla para toda la plataforma (decidido por
 * Raúl el 2026-10-28).
 *
 * - Un teléfono o un WhatsApp lleva **indicativo de país** y solo dígitos, y
 *   tiene que ser un número válido de ese país. Los países, sus indicativos y
 *   sus números salen de `libphonenumber` (la base de Google, la misma que usa
 *   el servidor con `phonenumbers`): están todos, no una lista escrita a mano.
 * - Si el indicativo no está, se escoge «Otro indicativo» y se escribe: entonces
 *   solo se pide lo que cumple cualquier número internacional (8 a 15 dígitos
 *   con el indicativo).
 * - Se guarda completo y sin espacios: `+573001234567` (la forma E.164, la que
 *   entiende WhatsApp). Lo guardado con espacios, guiones o sin «+» se lee igual.
 * - El correo, con la forma de un correo (`usuario@dominio.algo`).
 *
 * Copia de `nara-ui/src/contacto.js` mientras el ERP no use nara-ui: si se
 * cambia allá, se cambia aquí.
 */
import {
  getCountries, getCountryCallingCode, getExampleNumber, isValidPhoneNumber, parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js/max';
import ejemplos from 'libphonenumber-js/mobile/examples';

/** El código de «Otro indicativo» (uno que no está en la base, o que se quiere escribir a mano). */
export const OTRO = 'OTRO';

// Primero los de la región, en este orden; después el resto, por nombre.
const PRIMERO = ['CO', 'VE', 'EC', 'PE', 'PA', 'MX', 'US', 'CA', 'DO', 'PR', 'AR', 'BO', 'BR', 'CL', 'CR', 'CU',
  'SV', 'GT', 'HN', 'NI', 'PY', 'UY', 'ES'];
// Cuando varios países comparten indicativo y el número todavía no dice
// cuál es, el principal.
const PRINCIPAL = { 1: 'US', 7: 'RU', 39: 'IT', 44: 'GB', 47: 'NO', 61: 'AU', 212: 'MA', 262: 'RE', 290: 'SH',
  358: 'FI', 590: 'GP', 599: 'CW' };

let nombres = null;
function nombreDe(codigo) {
  try {
    nombres = nombres || new Intl.DisplayNames(['es'], { type: 'region' });
    return nombres.of(codigo) || codigo;
  } catch {
    return codigo;
  }
}

/** Todos los países: { codigo, nombre, indicativo }. «Otro indicativo» va al final. */
export const PAISES = (() => {
  const todos = getCountries().map((c) => ({ codigo: c, nombre: nombreDe(c), indicativo: getCountryCallingCode(c) }));
  const primero = PRIMERO.map((c) => todos.find((p) => p.codigo === c)).filter(Boolean);
  const resto = todos.filter((p) => !PRIMERO.includes(p.codigo))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  return [...primero, ...resto, { codigo: OTRO, nombre: 'Otro indicativo', indicativo: '' }];
})();

const POR_CODIGO = Object.fromEntries(PAISES.map((p) => [p.codigo, p]));
const POR_INDICATIVO = {};
PAISES.forEach((p) => { if (p.indicativo) (POR_INDICATIVO[p.indicativo] ||= []).push(p.codigo); });

// La zona horaria dice el país por defecto (la de la empresa, o la del
// navegador). Lo que no está aquí cae en Colombia.
const ZONAS = {
  'America/Bogota': 'CO', 'America/Caracas': 'VE', 'America/Guayaquil': 'EC', 'America/Lima': 'PE',
  'America/Panama': 'PA', 'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Monterrey': 'MX',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
  'America/Phoenix': 'US', 'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Santo_Domingo': 'DO',
  'America/Puerto_Rico': 'PR', 'America/Argentina/Buenos_Aires': 'AR', 'America/La_Paz': 'BO',
  'America/Sao_Paulo': 'BR', 'America/Santiago': 'CL', 'America/Costa_Rica': 'CR', 'America/Havana': 'CU',
  'America/El_Salvador': 'SV', 'America/Guatemala': 'GT', 'America/Tegucigalpa': 'HN',
  'America/Managua': 'NI', 'America/Asuncion': 'PY', 'America/Montevideo': 'UY', 'Europe/Madrid': 'ES',
  'Europe/Lisbon': 'PT', 'Europe/Paris': 'FR', 'Europe/Rome': 'IT', 'Europe/Berlin': 'DE',
  'Europe/London': 'GB', 'Europe/Amsterdam': 'NL', 'Europe/Zurich': 'CH',
};

export function paisPorDefecto(zona) {
  let z = zona;
  if (!z) {
    try { z = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { z = null; }
  }
  return ZONAS[z] || 'CO';
}

export const soloDigitos = (v) => String(v ?? '').replace(/\D/g, '');
export const paisDe = (codigo) => POR_CODIGO[codigo] || POR_CODIGO.CO;

/** Cuántos dígitos admite como máximo el número nacional de ese país (para no dejar escribir de más). */
export function maxDigitos(pais) {
  if (!pais || pais === OTRO) return 14;
  let max = 0;
  for (let n = 1; n <= 17; n += 1) {
    if (validatePhoneNumberLength('5'.repeat(n), pais) !== 'TOO_LONG') max = n;
  }
  return max || 15;
}

/**
 * Lo guardado → { pais, indicativo, digitos }. Con «+» (o «00», o con más
 * dígitos que un número del país por defecto) se toma el indicativo del
 * número; si varios países lo comparten, el que diga el número, el escogido
 * si lo comparte, o el principal. Un indicativo que no está en la base queda
 * como «Otro indicativo», con lo que se pueda separar.
 */
export function partirTelefono(valor, porDefecto = paisPorDefecto()) {
  const texto = String(valor ?? '').trim().replace(/^whatsapp:/i, '');
  const d = soloDigitos(texto);
  const base = porDefecto === OTRO ? 'CO' : (POR_CODIGO[porDefecto] ? porDefecto : 'CO');
  if (!d) return { pais: porDefecto || base, indicativo: paisDe(porDefecto).indicativo, digitos: '' };
  const conIndicativo = texto.startsWith('+') || texto.startsWith('00') || d.length > maxDigitos(base);
  if (!conIndicativo) return { pais: base, indicativo: paisDe(base).indicativo, digitos: d };
  const todo = texto.startsWith('00') ? d.slice(2) : d;
  // Los indicativos no se solapan (ninguno es el comienzo de otro): el primero que coincide es.
  for (let largo = 1; largo <= 3; largo += 1) {
    const ind = todo.slice(0, largo);
    const paises = POR_INDICATIVO[ind];
    if (!paises) continue;
    const digitos = todo.slice(largo);
    let pais = paises.length === 1 ? paises[0] : null;
    if (!pais) {
      const n = parsePhoneNumberFromString(`+${todo}`);
      pais = (n && n.country) || (paises.includes(porDefecto) ? porDefecto : PRINCIPAL[ind] || paises[0]);
    }
    return { pais, indicativo: ind, digitos };
  }
  // Fuera de la base: el indicativo escrito a mano no se puede separar del
  // número; el campo lo muestra en «Otro indicativo» con los primeros dígitos.
  return { pais: OTRO, indicativo: todo.slice(0, 3), digitos: todo.slice(3) };
}

/** País (o «Otro indicativo» con el suyo) y número → `+573001234567` ('' si no hay número). */
export function unirTelefono(pais, digitos, indicativo) {
  const d = soloDigitos(digitos);
  if (!d) return '';
  const ind = pais === OTRO ? soloDigitos(indicativo) : paisDe(pais).indicativo;
  return `+${ind}${d}`;
}

/** Un número de muestra del país (sin indicativo), para decir cómo se escribe. */
export function ejemploDe(pais) {
  try {
    return getExampleNumber(pais, ejemplos)?.nationalNumber || null;
  } catch {
    return null;
  }
}

/**
 * El motivo por el que el teléfono no sirve, o null si sirve (o está vacío).
 * `pais` sirve para los indicativos compartidos y para «Otro indicativo».
 */
export function errorTelefono(valor, pais) {
  const texto = String(valor ?? '').trim();
  const d = soloDigitos(texto);
  if (!d) return null;
  const partes = partirTelefono(texto, pais);
  if (partes.pais === OTRO || pais === OTRO) {
    if (d.length < 8 || d.length > 15) {
      return 'Con el indicativo, un número internacional tiene entre 8 y 15 dígitos.';
    }
    return null;
  }
  if (isValidPhoneNumber(`+${partes.indicativo}${partes.digitos}`)) return null;
  const p = paisDe(partes.pais);
  const ejemplo = ejemploDe(partes.pais);
  return `No es un número válido de ${p.nombre} (+${p.indicativo}): revisa los dígitos.`
    + (ejemplo ? ` Uno de ese país tiene ${ejemplo.length} dígitos, como ${ejemplo}.` : '');
}

// Usuario y dominio sin espacios, un solo «@», y el dominio con al menos un
// punto y una terminación de dos letras o más.
const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** El motivo por el que el correo no sirve, o null si sirve (o está vacío). */
export function errorCorreo(valor) {
  const v = String(valor ?? '').trim();
  if (!v) return null;
  return CORREO.test(v) ? null : 'Escribe un correo válido, como nombre@empresa.com.';
}
